import { LinkOutlined } from '@ant-design/icons';
import type { MenuDataItem, Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';
import React from 'react';
import { AvatarDropdown, AvatarName, Footer } from '@/components';
import { currentUser as queryCurrentUser } from '@/services/ant-design-pro/api';
import { getPublicPageByCode } from '@/services/shiyu-api/content';
import { getMenuTree, type Menu as APIMenu } from '@/services/shiyu-api/menu';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';
const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  menuData?: MenuDataItem[];
  complianceLine?: string;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const buildMenuData = (menus: APIMenu[]): MenuDataItem[] => {
    return (menus || [])
      .filter((m) => m.status === 1 && m.menu_type !== 'F')
      .map((m) => ({
        name: m.menu_name,
        path: m.path || `/${m.menu_code}`,
        children: m.children ? buildMenuData(m.children) : undefined,
      }));
  };
  const fetchMenuData = async () => {
    const token = localStorage.getItem('shiyu_token');
    if (!token) {
      return [];
    }
    try {
      const res = await getMenuTree();
      if (res.code === 200 && Array.isArray(res.data)) {
        return buildMenuData(res.data);
      }
    } catch (e) {
      // ignore and fallback to static menu
    }
    return [];
  };
  const fetchUserInfo = async () => {
    const token = localStorage.getItem('shiyu_token');
    if (!token) {
      return undefined;
    }
    try {
      const msg = await queryCurrentUser({
        skipErrorHandler: true,
      });
      return msg.data;
    } catch (_error) {
      history.push(loginPath);
    }
    return undefined;
  };
  const fetchSiteBranding = async () => {
    try {
      const res = await getPublicPageByCode('site_branding');
      if (res.code === 200 && res.data) {
        const baseTitle = res.data.title?.trim();
        const adminTitle = baseTitle
          ? `${baseTitle}${baseTitle.endsWith('后台管理系统') ? '' : '后台管理系统'}`
          : undefined;
        return {
          adminTitle,
          complianceLine: res.data.content?.trim(),
        };
      }
    } catch (e) {
      // ignore and fallback
    }
    return {
      adminTitle: undefined,
      complianceLine: undefined,
    };
  };
  // 如果不是登录页面，执行
  const { location } = history;
  const siteBranding = await fetchSiteBranding();
  const settings = {
    ...(defaultSettings as Partial<LayoutSettings>),
    ...(siteBranding.adminTitle ? { title: siteBranding.adminTitle } : {}),
    logo: undefined,
  };
  if (![loginPath, '/user/register', '/user/register-result'].includes(location.pathname)) {
    const currentUser = await fetchUserInfo();
    const menuData = await fetchMenuData();
    const complianceLine = siteBranding.complianceLine || '备案 京ICP备2026号 ｜ © 2026-至今 Mr.Wang';
    return {
      fetchUserInfo,
      currentUser,
      menuData,
      complianceLine,
      settings,
    };
  }
  return {
    fetchUserInfo,
    settings,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({ initialState, setInitialState }) => {
  return {
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: initialState?.currentUser?.name,
    },
    footerRender: () => <Footer complianceLine={initialState?.complianceLine} />,
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isDev
      ? [
          <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
            <LinkOutlined />
            <span>OpenAPI 文档</span>
          </Link>,
        ]
      : [],
    menuHeaderRender: undefined,
    menuDataRender: (menuData) => {
      if (initialState?.menuData && initialState.menuData.length > 0) {
        return initialState.menuData;
      }
      // 后端未返回菜单时，仅展示欢迎页
      return [
        {
          name: '欢迎',
          path: '/welcome',
          icon: 'smile',
        },
      ];
    },
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  // 开发环境使用代理。生产环境优先读取配置；若误配置为 localhost 且当前并非本机访问，则回退同源 /api。
  baseURL:
    process.env.NODE_ENV === 'production'
      ? (() => {
          const configured = (process.env.REACT_APP_API_BASE_URL || '').trim();
          if (!configured) {
            return '';
          }
          if (typeof window === 'undefined') {
            return configured;
          }
          const currentIsLocal = /^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname);
          const configuredIsLocal = /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/i.test(configured);
          if (configuredIsLocal && !currentIsLocal) {
            return '';
          }
          return configured;
        })()
      : '',
  ...errorConfig,
};
